/**
 * Region selection on the live page before capture (Phase 2 follow-up): a dimmed overlay where
 * the user drags a rectangle. Resolves with the region in viewport CSS pixels, `'full'` for the
 * whole page, or `null` when cancelled (Esc / Vazgeç). Lives in the main bundle; tiny.
 */
export interface Region {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface SelectorStrings {
  hint: string;
  full: string;
  cancel: string;
}

const MIN_SIZE = 8;

export function selectRegion(root: ShadowRoot | HTMLElement, t: SelectorStrings): Promise<Region | 'full' | null> {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'fe-region';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-label', t.hint);
    overlay.innerHTML =
      `<div class="fe-region-bar"><span>${escape(t.hint)}</span>` +
      `<button type="button" class="fe-btn" data-act="full">${escape(t.full)}</button>` +
      `<button type="button" class="fe-btn" data-act="cancel">${escape(t.cancel)}</button></div>` +
      `<div class="fe-region-box" hidden></div>`;
    const box = overlay.querySelector<HTMLElement>('.fe-region-box')!;
    const bar = overlay.querySelector<HTMLElement>('.fe-region-bar')!;

    let start: { x: number; y: number } | null = null;
    let current: Region | null = null;

    const finish = (result: Region | 'full' | null) => {
      cleanup();
      resolve(result);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        finish(null);
      }
    };
    const paint = () => {
      if (!current) return;
      box.hidden = false;
      box.style.left = current.x + 'px';
      box.style.top = current.y + 'px';
      box.style.width = current.width + 'px';
      box.style.height = current.height + 'px';
    };
    const onDown = (e: PointerEvent) => {
      if ((e.target as Element).closest('.fe-region-bar')) return;
      e.preventDefault();
      start = { x: e.clientX, y: e.clientY };
      current = { x: e.clientX, y: e.clientY, width: 0, height: 0 };
      bar.style.opacity = '0.25';
      // While dragging, the box's shadow dims everything outside it; the selection itself stays clear.
      overlay.style.background = 'transparent';
      overlay.setPointerCapture?.(e.pointerId);
      paint();
    };
    const onMove = (e: PointerEvent) => {
      if (!start) return;
      current = {
        x: Math.min(start.x, e.clientX),
        y: Math.min(start.y, e.clientY),
        width: Math.abs(e.clientX - start.x),
        height: Math.abs(e.clientY - start.y),
      };
      paint();
    };
    const onUp = () => {
      if (!start) return;
      const region = current;
      start = null;
      bar.style.opacity = '';
      if (region && region.width >= MIN_SIZE && region.height >= MIN_SIZE) finish(clamp(region));
      else {
        current = null;
        box.hidden = true;
        overlay.style.background = '';
      }
    };
    const onClick = (e: Event) => {
      const act = (e.target as Element).closest<HTMLElement>('[data-act]')?.dataset.act;
      if (act === 'full') finish('full');
      if (act === 'cancel') finish(null);
    };
    const cleanup = () => {
      overlay.removeEventListener('pointerdown', onDown);
      overlay.removeEventListener('pointermove', onMove);
      overlay.removeEventListener('pointerup', onUp);
      overlay.removeEventListener('pointercancel', onUp);
      overlay.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKey, true);
      overlay.remove();
    };

    overlay.addEventListener('pointerdown', onDown);
    overlay.addEventListener('pointermove', onMove);
    overlay.addEventListener('pointerup', onUp);
    overlay.addEventListener('pointercancel', onUp);
    overlay.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey, true);
    root.appendChild(overlay);
    overlay.querySelector<HTMLButtonElement>('[data-act="full"]')?.focus();
  });
}

function clamp(r: Region): Region {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const x = Math.max(0, r.x);
  const y = Math.max(0, r.y);
  return { x, y, width: Math.min(r.width - (x - r.x), vw - x), height: Math.min(r.height - (y - r.y), vh - y) };
}

const escape = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
